import type { Schema as ZeroSchema } from "@rocicorp/zero";
import type { zeroDrizzle } from "@rocicorp/zero/server/adapters/drizzle";
import {
  createZeroDataTransformer,
  type ArgsTransformer,
} from "@corpuscomplete/zero-data-transformer";
import { parse, stringify } from "devalue";
import { schema as genSchema } from "./schema.gen";
import type { UserActor } from "../actor";
import type { AnyAgentRegistry } from "../ai/agent-definition";

const grants = [
  {
    sourceField: ["id"],
    destField: ["resourceId"],
    destSchema: "accessGrant",
    cardinality: "many",
  },
] as const;

const grantsByCollection = [
  {
    sourceField: ["collectionId"],
    destField: ["resourceId"],
    destSchema: "accessGrant",
    cardinality: "many",
  },
] as const;

const grantsByScope = [
  {
    sourceField: ["scopeId"],
    destField: ["resourceId"],
    destSchema: "accessGrant",
    cardinality: "many",
  },
] as const;

const grantsByUser = [
  {
    sourceField: ["userId"],
    destField: ["resourceId"],
    destSchema: "accessGrant",
    cardinality: "many",
  },
] as const;

const grantsByGroup = [
  {
    sourceField: ["groupId"],
    destField: ["resourceId"],
    destSchema: "accessGrant",
    cardinality: "many",
  },
] as const;

export const schema = {
  ...genSchema,
  relationships: {
    ...genSchema.relationships,
    foodMeta: {
      ...genSchema.relationships.foodMeta,
      grants,
      grantsByCollection,
    },
    foodCollection: {
      ...genSchema.relationships.foodCollection,
      grants,
      grantsByScope,
    },
    foodLogEntry: {
      ...genSchema.relationships.foodLogEntry,
      grantsByUser,
    },
    foodLogSourceGroup: {
      ...genSchema.relationships.foodLogSourceGroup,
      grantsByUser,
    },
    nutritionProgram: {
      ...genSchema.relationships.nutritionProgram,
      grantsByUser,
    },
    list: {
      ...genSchema.relationships.list,
      grantsByCollection,
      grants,
    },
    listCollection: {
      ...genSchema.relationships.listCollection,
      grants,
    },
    bodyCharacteristic: {
      // ...genSchema.relationships.bodyCharacteristic,
      grantsByUser,
    },
    bodyMetricEntry: {
      ...genSchema.relationships.bodyMetricEntry,
      grantsByUser,
    },
    bodyMetricGoal: {
      ...genSchema.relationships.bodyMetricGoal,
      grantsByUser,
    },
    group: {
      ...genSchema.relationships.group,
      grants,
    },
    space: {
      ...genSchema.relationships.space,
      grants,
    },
    mealPlanningSession: {
      ...genSchema.relationships.mealPlanningSession,
      grants,
    },
    groupInvite: {
      ...genSchema.relationships.groupInvite,
      grantsByGroup,
    },
    groupMember: {
      ...genSchema.relationships.groupMember,
      grantsByGroup,
      grantsByUser,
      profile: [
        {
          sourceField: ["userId"],
          destField: ["userId"],
          destSchema: "userProfile",
          cardinality: "one",
        },
      ],
    },
    accessGrant: {
      // ...genSchema.relationships.grant,
      granteeGroupMembers: [
        {
          sourceField: ["granteeId"],
          destField: ["groupId"],
          destSchema: "groupMember",
          cardinality: "many",
        },
      ],
    },
  },
} as const satisfies ZeroSchema;

export type Schema = typeof schema;

export type Context = {
  actor: UserActor;
  agentRegistry: AnyAgentRegistry;
};

export const argsTransformer = {
  serialize(value) {
    return stringify(value);
  },
  deserialize(value) {
    return typeof value === "string" ? parse(value) : value;
  },
} satisfies ArgsTransformer;

export const zeroDataTransformer = createZeroDataTransformer<Schema, Context>({
  schema,
  argsTransformer,
});

export const zql = zeroDataTransformer.createQueryBuilder();

declare module "@rocicorp/zero" {
  interface DefaultTypes {
    schema: typeof schema;
    context: Context;
    dbProvider: ReturnType<typeof zeroDrizzle>;
  }
}
